import React from 'react';
import { WizardStepProps } from '../../../types';

const BasicInformationStep: React.FC<WizardStepProps> = ({
  state,
  updateState,
  onNext,
  errors = {}
}) => {
  const handleCampaignNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateState({ campaign_name: event.target.value });
  };

  const handleGoalSelect = (goal: 'product_discovery' | 'lead_generation') => {
    updateState({ campaignGoal: goal });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (state.campaign_name.trim().length >= 3 && state.campaignGoal) {
      onNext();
    }
  };

  const canProceed = state.campaign_name.trim().length >= 3 && !!state.campaignGoal;

  return (
    <div className="p-8">
      <form onSubmit={handleSubmit} className="max-w-2xl">
        {/* Campaign Name */}
        <div className="mb-6">
          <label htmlFor="campaign_name" className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Name *
          </label>
          <input
            type="text"
            id="campaign_name"
            value={state.campaign_name}
            onChange={handleCampaignNameChange}
            placeholder="Enter a descriptive name for your campaign"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
              errors.campaign_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            maxLength={100}
            autoFocus
          />
          {errors.campaign_name && (
            <div className="mt-2 text-sm text-red-600">
              {errors.campaign_name.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}
          <div className="mt-2 text-sm text-gray-500">
            {state.campaign_name.length}/100 characters
            {state.campaign_name.length > 90 && (
              <span className="text-yellow-600 ml-2">Approaching limit</span>
            )}
          </div>
        </div>

        {/* Campaign Goal */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Campaign Goal *
          </label>
          <p className="text-sm text-gray-500 mb-4">
            This determines the type of weekly report you receive and how your AI Analyzer is tuned.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Product Discovery */}
            <button
              type="button"
              onClick={() => handleGoalSelect('product_discovery')}
              className={`p-5 text-left border-2 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                state.campaignGoal === 'product_discovery'
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
              }`}
            >
              <div className="text-2xl mb-2">🔬</div>
              <div className={`font-semibold text-sm mb-1 ${
                state.campaignGoal === 'product_discovery' ? 'text-blue-800' : 'text-gray-900'
              }`}>
                Product Discovery
              </div>
              <div className={`text-xs leading-relaxed ${
                state.campaignGoal === 'product_discovery' ? 'text-blue-700' : 'text-gray-500'
              }`}>
                Understand your market. Uncover buyer pain points, validate assumptions, and gather real-world signal from prospects.
              </div>
              {state.campaignGoal === 'product_discovery' && (
                <div className="mt-3 flex items-center gap-1 text-xs font-medium text-blue-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Selected
                </div>
              )}
            </button>

            {/* Lead Generation */}
            <button
              type="button"
              onClick={() => handleGoalSelect('lead_generation')}
              className={`p-5 text-left border-2 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                state.campaignGoal === 'lead_generation'
                  ? 'border-indigo-500 bg-indigo-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm'
              }`}
            >
              <div className="text-2xl mb-2">🎯</div>
              <div className={`font-semibold text-sm mb-1 ${
                state.campaignGoal === 'lead_generation' ? 'text-indigo-800' : 'text-gray-900'
              }`}>
                Lead Generation
              </div>
              <div className={`text-xs leading-relaxed ${
                state.campaignGoal === 'lead_generation' ? 'text-indigo-700' : 'text-gray-500'
              }`}>
                Drive pipeline. Convert outreach into booked meetings, demos, and qualified opportunities.
              </div>
              {state.campaignGoal === 'lead_generation' && (
                <div className="mt-3 flex items-center gap-1 text-xs font-medium text-indigo-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Selected
                </div>
              )}
            </button>
          </div>
          {!state.campaignGoal && state.campaign_name.trim().length >= 3 && (
            <p className="mt-3 text-sm text-amber-600">
              Please select a campaign goal to continue.
            </p>
          )}
        </div>

        {/* Campaign Name Suggestions */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Suggested naming patterns:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm text-gray-900">Target-based</div>
              <div className="text-xs text-gray-600 mt-1">
                "Tech Executives Q4 2025"<br />
                "SF Startup Founders"
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm text-gray-900">Campaign-based</div>
              <div className="text-xs text-gray-600 mt-1">
                "Product Launch Outreach"<br />
                "Holiday Sales Push"
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm text-gray-900">Service-based</div>
              <div className="text-xs text-gray-600 mt-1">
                "Junior SDR - Healthcare"<br />
                "Top Qualifier - FinTech"
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-sm text-gray-900">Date-based</div>
              <div className="text-xs text-gray-600 mt-1">
                "Q4 2025 Enterprise"<br />
                "January Warm Leads"
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 mb-2">💡 Best Practices</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Keep it descriptive but concise (3-50 characters maximum)</li>
            <li>• Include target audience or campaign purpose</li>
            <li>• Avoid special characters that might cause issues</li>
            <li>• Consider adding dates for time-sensitive campaigns</li>
          </ul>
        </div>

        {/* Campaign Preview */}
        {state.campaign_name.length > 0 && (
          <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-sm font-medium text-green-900 mb-2">Campaign Preview</h3>
            <div className="text-sm text-green-700">
              <strong>Name:</strong> {state.campaign_name}
            </div>
            {state.campaignGoal && (
              <div className="text-sm text-green-700 mt-1">
                <strong>Goal:</strong> {state.campaignGoal === 'product_discovery' ? '🔬 Product Discovery' : '🎯 Lead Generation'}
              </div>
            )}
            <div className="text-sm text-green-700 mt-1">
              <strong>Created:</strong> {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
        )}

        {/* Hidden submit to allow Enter key on campaign name input */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} disabled={!canProceed} />
      </form>
    </div>
  );
};

export default BasicInformationStep;
